import { useEffect, useMemo } from 'react';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import { useInitiativeCompletions } from '../../../api/portfolio-client/portfolio-client.hooks';
import type {
  InitiativesFocusResponse,
  ProcessAnalysisInitiativeChartData,
} from '../../../api/portfolio-client/portfolio-client.type';
import {
  useProjectInitiatives,
  useProjectInitiativesCompletions,
} from '../../../api/projects-client/projects-client.hooks';
import { useWorkPeriodsMeasureSingular } from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  Measure,
  TimeAllocationType,
  type UseWorkPeriodsMeasurePayloadSingular,
} from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  setInitiativeCompletionData,
  setInitiativeFocusData,
  setInitiatives,
} from '../../../store/process-analysis-store/process-analysis-store.actions';
import { Tab } from '../process-analysis.type';

/**
 * Custom hook that fetches initiatives data.
 *
 * @returns {object} Loading flags. `isLoading` is true only on first paint
 *   with no cached data; `isFetching` stays true on background refetches.
 */
const useInitiativesData = (): {
  isFetching: boolean;
  isLoading: boolean;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);

  const { initiatives: portfolioInitiatives, query: portfolioQuery } = useInitiatives(portfolio?.id, {
    queryKey: ['portfolioInitiatives', portfolio?.id] as const,
    enabled: activeTab === Tab.Portfolios && !!portfolio?.id,
  });

  const { initiatives: projectInitiatives, query: projectQuery } = useProjectInitiatives(selectedTeams[0], {
    queryKey: ['projectInitiatives', selectedTeams[0]] as const,
    enabled: activeTab === Tab.Teams && selectedTeams.length > 0,
  });

  useEffect(() => {
    switch (activeTab) {
      case Tab.Portfolios:
        return setInitiatives(portfolioInitiatives || []);
      case Tab.Teams:
        return setInitiatives(projectInitiatives || []);
      default:
        return setInitiatives([]);
    }
  }, [portfolioInitiatives, projectInitiatives, activeTab]);

  return {
    isFetching: portfolioQuery.isFetching || projectQuery.isFetching,
    isLoading: portfolioQuery.isLoading || projectQuery.isLoading,
  };
};

/**
 * Fetches initiative completion data for either the selected portfolios or teams based on the active tab.
 *
 * @returns {{ data: ProcessAnalysisInitiativeChartData | undefined; isFetching: boolean }} - Object containing the initiative completion data and a boolean indicating whether the data is being fetched
 */
const useInitiativeCompletionData = (): {
  data: ProcessAnalysisInitiativeChartData | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  const { initiativeCompletions: portfolioInitiativeCompletions, query: portfolioInitiativeCompletionsQuery } =
    useInitiativeCompletions(portfolio?.id || '', formatToISODate(startDate), formatToISODate(endDate), {
      enabled: activeTab === Tab.Portfolios && !!portfolio?.id,
      staleTime: 1000 * 60 * 5,
    });

  const { initiativeCompletions: projectInitiativeCompletions, query: projectInitiativeCompletionsQuery } =
    useProjectInitiativesCompletions(selectedTeams[0], formatToISODate(startDate), formatToISODate(endDate), {
      enabled: activeTab === Tab.Teams && !!selectedTeams[0],
      staleTime: 1000 * 60 * 5,
    });

  const data = activeTab === Tab.Portfolios ? portfolioInitiativeCompletions : projectInitiativeCompletions;
  const isFetching = portfolioInitiativeCompletionsQuery.isFetching || projectInitiativeCompletionsQuery.isFetching;
  const isLoading = portfolioInitiativeCompletionsQuery.isLoading || projectInitiativeCompletionsQuery.isLoading;

  useEffect(() => {
    if (data) {
      setInitiativeCompletionData(data);
    }
  }, [data]);

  return { data, isFetching, isLoading };
};

/**
 * Hook to fetch initiative focus data for the current context
 *
 * @returns {{ data: ProcessAnalysisInitiativeChartData | undefined; isFetching: boolean }} Object containing the initiative focus data and a boolean indicating whether the data is being fetched
 */
const useInitiativeFocusData = (): {
  data: ProcessAnalysisInitiativeChartData | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  const payload: UseWorkPeriodsMeasurePayloadSingular = {
    measure_name: [Measure.InitiativesFocus],
    start_date: formatToISODate(startDate) || undefined,
    end_date: formatToISODate(endDate) || undefined,
    time_allocation_type: TimeAllocationType.Monthly,
    queryKey: ['initiative-focus', activeTab, portfolio?.id, selectedTeams[0], startDate, endDate],
    enabled: activeTab === Tab.Portfolios || activeTab === Tab.Teams,
  };

  const { data, query } = useWorkPeriodsMeasureSingular<InitiativesFocusResponse>({
    payload:
      activeTab === Tab.Portfolios
        ? { ...payload, portfolio_id: portfolio?.id }
        : { ...payload, project_id: selectedTeams[0] },
  });

  const chartData = useMemo(() => {
    const initiatives = data?.initiatives_focus?.initiatives ? Object.values(data.initiatives_focus.initiatives) : [];
    const labels = data?.initiatives_focus?.labels ? data.initiatives_focus.labels : [];

    return { initiatives, labels };
  }, [data]);

  useEffect(() => {
    if (data?.initiatives_focus) {
      setInitiativeFocusData(chartData);
    }
  }, [data, chartData]);

  return { data: chartData, isFetching: query.isFetching, isLoading: query.isLoading };
};

export { useInitiativeCompletionData, useInitiativeFocusData, useInitiativesData };
